import { useState, useEffect } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  Archive,
  ArchiveRestore,
  GripVertical,
  Save,
  X,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import adminApi from '@/lib/adminApi';

const MembersManager = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewMember, setPreviewMember] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');

  const [formData, setFormData] = useState({
    name: '',
    category: 'founder',
    logo_url: '',
    website: '',
    is_visible: true,
    order: 0
  });

  const fetchMembers = async () => {
    try {
      const response = await adminApi.get(`/members?visible_only=false`);
      setMembers(response.data);
    } catch (error) {
      console.error('Error fetching members:', error);
      toast.error('Erreur lors du chargement des membres');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const openAddDialog = () => {
    setEditingMember(null);
    setFormData({
      name: '',
      category: 'founder',
      logo_url: '',
      website: '',
      is_visible: true,
      order: members.length + 1
    });
    setDialogOpen(true);
  };

  const openEditDialog = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name,
      category: member.category,
      logo_url: member.logo_url || '',
      website: member.website || '',
      is_visible: member.is_visible,
      order: member.order
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Le nom est requis');
      return;
    }

    setSaving(true);
    try {
      if (editingMember) {
        await adminApi.put(`/members/${editingMember.id}`, formData);
        toast.success('Membre mis à jour');
      } else {
        await adminApi.post(`/members`, formData);
        toast.success('Membre ajouté');
      }
      setDialogOpen(false);
      fetchMembers();
    } catch (error) {
      console.error('Error saving member:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!memberToDelete) return;

    try {
      await adminApi.delete(`/members/${memberToDelete.id}`);
      toast.success('Membre supprimé');
      setDeleteDialogOpen(false);
      setMemberToDelete(null);
      fetchMembers();
    } catch (error) {
      console.error('Error deleting member:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleVisibility = async (member) => {
    try {
      await adminApi.put(`/members/${member.id}`, {
        is_visible: !member.is_visible
      });
      fetchMembers();
      toast.success(`Membre ${member.is_visible ? 'masqué' : 'affiché'}`);
    } catch (error) {
      console.error('Error toggling visibility:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const openPreview = (member) => {
    setPreviewMember(member);
    setPreviewDialogOpen(true);
  };

  const filteredMembers = members.filter(m => {
    if (filter === 'all') return true;
    if (filter === 'visible') return m.is_visible;
    if (filter === 'hidden') return !m.is_visible;
    return m.category === filter;
  });

  const founderCount = members.filter(m => m.category === 'founder').length;
  const activeCount = members.filter(m => m.category === 'active').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-[#1e3a8a]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="members-manager">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-playfair text-2xl font-semibold text-[#0F172A]">
            Gestion des membres
          </h2>
          <p className="text-slate-600 font-inter text-sm mt-1">
            {founderCount} fondateurs • {activeCount} actifs
          </p>
        </div>
        <Button
          onClick={openAddDialog}
          className="bg-[#1e3a8a] hover:bg-[#172554]"
          data-testid="add-member-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Ajouter un membre
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {[
          { value: 'all', label: 'Tous' },
          { value: 'founder', label: 'Fondateurs' },
          { value: 'active', label: 'Actifs' },
          { value: 'visible', label: 'Visibles' },
          { value: 'hidden', label: 'Masqués' }
        ].map((f) => (
          <Button
            key={f.value}
            variant={filter === f.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f.value)}
            className={filter === f.value ? 'bg-[#1e3a8a]' : ''}
            data-testid={`filter-${f.value}`}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {/* Members List */}
      <Card>
        <CardContent className="p-0">
          <div className="divide-y">
            {filteredMembers.length === 0 ? (
              <div className="p-8 text-center text-slate-500 font-inter">
                Aucun membre trouvé
              </div>
            ) : (
              filteredMembers.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors"
                  data-testid={`member-row-${member.id}`}
                >
                  <GripVertical className="w-5 h-5 text-slate-300 cursor-move" />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-inter font-medium text-[#0F172A] truncate">
                        {member.name}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        member.category === 'founder'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {member.category === 'founder' ? 'Fondateur' : 'Actif'}
                      </span>
                    </div>
                    {member.logo_url && (
                      <span className="text-xs text-slate-500 font-inter">Logo configuré</span>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openPreview(member)}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      title="Prévisualiser"
                      data-testid={`preview-btn-${member.id}`}
                    >
                      <Eye size={18} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleVisibility(member)}
                      className={member.is_visible ? 'text-green-600' : 'text-slate-400'}
                      title={member.is_visible ? 'Archiver' : 'Désarchiver'}
                      data-testid={`visibility-btn-${member.id}`}
                    >
                      {member.is_visible ? <Archive size={18} /> : <ArchiveRestore size={18} />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(member)}
                      title="Modifier"
                      data-testid={`edit-btn-${member.id}`}
                    >
                      <Pencil size={18} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setMemberToDelete(member);
                        setDeleteDialogOpen(true);
                      }}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="Supprimer"
                      data-testid={`delete-btn-${member.id}`}
                    >
                      <Trash2 size={18} />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="member-dialog">
          <DialogHeader>
            <DialogTitle className="font-playfair">
              {editingMember ? 'Modifier le membre' : 'Ajouter un membre'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="member-name" className="font-inter text-sm">Nom *</Label>
              <Input
                id="member-name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="mt-2"
                placeholder="Nom du membre"
                data-testid="member-name-input"
              />
            </div>

            <div>
              <Label htmlFor="member-category" className="font-inter text-sm">Catégorie *</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger className="mt-2" data-testid="member-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="founder">Membre fondateur / soutien</SelectItem>
                  <SelectItem value="active">Membre actif</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="member-logo" className="font-inter text-sm">URL du logo</Label>
              <Input
                id="member-logo"
                value={formData.logo_url}
                onChange={(e) => setFormData(prev => ({ ...prev, logo_url: e.target.value }))}
                className="mt-2"
                placeholder="https://..."
                data-testid="member-logo-input"
              />
            </div>

            <div>
              <Label htmlFor="member-website" className="font-inter text-sm">Site web</Label>
              <Input
                id="member-website"
                value={formData.website}
                onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                className="mt-2"
                placeholder="https://..."
                data-testid="member-website-input"
              />
            </div>

            <div>
              <Label htmlFor="member-order" className="font-inter text-sm">Ordre d'affichage</Label>
              <Input
                id="member-order"
                type="number"
                value={formData.order}
                onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                className="mt-2"
                data-testid="member-order-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#1e3a8a] hover:bg-[#172554]"
              data-testid="save-member-btn"
            >
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce membre ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le membre "{memberToDelete?.name}" sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="member-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-playfair">
              Fiche membre
            </DialogTitle>
          </DialogHeader>

          {previewMember && (
            <div className="space-y-6 py-4">
              {/* Logo preview */}
              <div className="flex justify-center">
                {previewMember.logo_url ? (
                  <div className="bg-slate-50 p-6 rounded-lg">
                    <img
                      src={previewMember.logo_url}
                      alt={previewMember.name}
                      className="max-h-24 max-w-full object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="hidden items-center justify-center h-24 text-slate-400 font-inter text-sm">
                      Logo non disponible
                    </div>
                  </div>
                ) : (
                  <div className="bg-slate-50 p-6 rounded-lg h-24 w-48 flex items-center justify-center">
                    <span className="text-slate-400 font-inter text-sm">Pas de logo</span>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="space-y-4">
                <div>
                  <Label className="text-xs text-slate-500 uppercase tracking-wide">Nom</Label>
                  <p className="font-inter font-medium text-lg text-[#0F172A]">{previewMember.name}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500 uppercase tracking-wide">Catégorie</Label>
                    <p className="font-inter text-[#0F172A]">
                      <span className={`inline-flex items-center px-2 py-1 rounded text-sm ${
                        previewMember.category === 'founder'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {previewMember.category === 'founder' ? 'Fondateur / Soutien' : 'Actif'}
                      </span>
                    </p>
                  </div>

                  <div>
                    <Label className="text-xs text-slate-500 uppercase tracking-wide">Visibilité</Label>
                    <p className="font-inter">
                      <span className={`inline-flex items-center gap-1 text-sm ${
                        previewMember.is_visible ? 'text-green-600' : 'text-slate-400'
                      }`}>
                        {previewMember.is_visible ? <Eye size={14} /> : <EyeOff size={14} />}
                        {previewMember.is_visible ? 'Visible' : 'Masqué'}
                      </span>
                    </p>
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-slate-500 uppercase tracking-wide">Site web</Label>
                  {previewMember.website ? (
                    <a 
                      href={previewMember.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="font-inter text-blue-600 hover:underline block truncate"
                    >
                      {previewMember.website}
                    </a>
                  ) : (
                    <p className="font-inter text-slate-400 italic">Non renseigné</p>
                  )}
                </div>

                <div>
                  <Label className="text-xs text-slate-500 uppercase tracking-wide">URL du logo</Label>
                  {previewMember.logo_url ? (
                    <p className="font-inter text-slate-600 text-sm truncate">{previewMember.logo_url}</p>
                  ) : (
                    <p className="font-inter text-slate-400 italic">Non renseigné</p>
                  )}
                </div>

                <div>
                  <Label className="text-xs text-slate-500 uppercase tracking-wide">Ordre d'affichage</Label>
                  <p className="font-inter text-[#0F172A]">{previewMember.order}</p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setPreviewDialogOpen(false);
                openEditDialog(previewMember);
              }}
            >
              <Pencil className="w-4 h-4 mr-2" />
              Modifier
            </Button>
            <Button
              onClick={() => setPreviewDialogOpen(false)}
              className="bg-[#1e3a8a] hover:bg-[#172554]"
            >
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MembersManager;
